import { reaction } from 'mobx';
import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { IImportColumn, Importer, IPreviewCleanedCell, IPreviewRow } from '@feathr/blackbox';

import {
  getCleanedExamples,
  getPreviewFailureReason,
  getPreviewMappingKey,
  getSampleValues,
  getUnreadableValues,
  isPreviewRetryable,
  matchBuiltInField,
} from './DataImportStepTwo.utils';

/** Why a preview failed to load. */
export interface IPreviewFailure {
  message: string;
  /** The server's sentence, when it refused the preview. */
  reason?: string;
  isRetryable: boolean;
}

/** The Map step's preview, as the step renders it. */
export interface IUseMappingPreviewReturn {
  /** The latest preview's rows; undefined while a preview loads. */
  rows?: IPreviewRow[];
  // Kept across remaps and failed previews: neither has rows to sample.
  samples?: Record<string, string>;
  /** Each column the preview could not read, against the value it refused. Cleared on a remap, unlike `samples`. */
  unreadable?: Map<string, string>;
  /** Each column the preview cleaned symbols from, against the first such cell. Cleared on a remap. */
  cleaned?: Map<string, IPreviewCleanedCell>;
  /** Why the latest preview failed; cleared when a preview succeeds. */
  failure?: IPreviewFailure;
  /** Sends the preview again with the mappings unchanged. */
  retry: () => void;
}

interface IPreviewState extends IUseMappingPreviewReturn {
  /** Part of the preview's key, so a retry reloads it with the mappings unchanged. */
  retryCount: number;
  setRows: (rows?: IPreviewRow[]) => void;
  setFailure: (failure?: IPreviewFailure) => void;
}

/**
 * Loads the Map step's preview and keeps it in step with the mappings. Encapsulates:
 * - mapping built-in fields by heading before the first preview, unless the step is disabled
 * - one preview per mapping change, where the latest request wins
 * - the failure, and a retry that resends the same mappings
 * - the samples, which outlive a remap and a failed preview
 *
 * Returns the live observable, not a copy: a reader that isn't an observer, such as `renderHook`,
 * would otherwise see a stale snapshot.
 *
 * @param importer - The import being mapped.
 * @param disabled - Whether the step is read-only. Read once, when the preview starts.
 * @returns The preview state and `retry`.
 *
 * @example
 * ```tsx
 * const preview = useMappingPreview(importer, disabled);
 * const isLoading = preview.rows === undefined;
 * ```
 */
export function useMappingPreview(importer: Importer, disabled: boolean): IUseMappingPreviewReturn {
  const { t } = useTranslation();

  const state = useLocalObservable<IPreviewState>(() => ({
    rows: undefined,
    samples: undefined,
    failure: undefined,
    retryCount: 0,
    setRows: (rows?: IPreviewRow[]): void => {
      state.rows = rows;
      state.unreadable = rows ? getUnreadableValues(rows) : undefined;
      state.cleaned = rows ? getCleanedExamples(rows) : undefined;
      if (rows && !state.failure) {
        state.samples = getSampleValues(rows, importer.get('column_names'));
      }
    },
    setFailure: (failure?: IPreviewFailure): void => {
      state.failure = failure;
    },
    retry: (): void => {
      state.retryCount += 1;
    },
  }));

  useEffect(() => {
    // Map built-in fields here too, so the first preview doesn't depend on the rows having mounted.
    if (!disabled) {
      const columns: IImportColumn[] = importer.get('column_mappers');
      const matched = columns.map(matchBuiltInField).some(Boolean);
      if (matched) {
        importer.setAttributeDirty('column_mappers');
      }
    }

    // Requests can overlap, and an earlier one may resolve last.
    let latestRequest = 0;

    const dispose = reaction(
      // completedColumnMappers alone only changes with a field, not with the column it reads.
      () => `${getPreviewMappingKey(importer.completedColumnMappers)}|${state.retryCount}`,
      async () => {
        const requestId = ++latestRequest;
        state.setRows(undefined);
        let previewRows: IPreviewRow[];
        let previewFailure: IPreviewFailure | undefined;
        try {
          previewRows = await importer.preview(importer.completedColumnMappers);
        } catch (error) {
          // Settle on an empty preview, not a spinner that never ends, and say why it's empty.
          previewRows = [];
          previewFailure = {
            message: error instanceof Error && error.message ? error.message : t('Unknown error.'),
            reason: getPreviewFailureReason(error),
            isRetryable: isPreviewRetryable(error),
          };
        }
        if (requestId === latestRequest) {
          state.setFailure(previewFailure);
          state.setRows(previewRows);
        }
      },
      { fireImmediately: true },
    );

    return (): void => {
      latestRequest += 1;
      dispose();
    };
    // Not on `disabled`: re-running would rebuild the reaction and send another preview.
  }, [importer, state, t]);

  return state;
}
